import React, { useMemo, useState } from 'react';
import axiosInstance from './../axiosInstance.js';
import { Button, Modal, NumberInput, Text } from '@mantine/core';
import AdminRatingHistoryButton from './AdminRatingHistoryButton';

// Ordered list of platforms the admin can set ratings on. Names match the
// icon keys used by PlatformRatings and the ratingPlatformFromName map on
// the backend.
const PLATFORMS = [
  { name: 'imdb', label: 'IMDb', icon: '/imdb_small_icon.png' },
  { name: 'kinopoisk', label: 'Kinopoisk', icon: '/kinopoisk_small_icon.png' },
  { name: 'rottenTomatoes', label: 'Rotten Tomatoes (critics)', icon: '/rottentomatoes_icon.png' },
  { name: 'rottenTomatoesAudience', label: 'Rotten Tomatoes (audience)', icon: '/rottentomatoes_icon.png' },
  { name: 'amazon', label: 'Amazon', icon: '/amazon_icon.png' },
  { name: 'goodreads', label: 'Goodreads', icon: '/goodreads_icon.png' },
  { name: 'reddit', label: 'Reddit', icon: '/reddit_icon.png' },
  { name: 'appleBooks', label: 'Apple Books', icon: '/applebooks_icon.png' },
  { name: 'steam', label: 'Steam', icon: '/steam_icon.png' },
  { name: 'metacritic', label: 'Metacritic (critics)', icon: '/metacritic_icon.png' },
  { name: 'metacriticUser', label: 'Metacritic (users)', icon: '/metacritic_icon.png' },
  { name: 'livelib', label: 'Livelib', icon: '/livelib_icon.png' },
  { name: 'litres', label: 'Litres', icon: '/litres_icon.png' },
];

// AdminRatingsEditor renders a small table of every platform, with the
// current rating/votes pre-filled and inline inputs to overwrite or
// delete. Admin-only — parent gates on isAdmin before rendering.
// Collapsed by default: the whole panel is behind a toggle so it doesn't
// clutter the art page during normal browsing.
const AdminRatingsEditor = ({ art, onArtUpdated }) => {
  const [open, setOpen] = useState(false);

  const infosByName = useMemo(() => {
    const m = {};
    (art.otherPlatformInfos || []).forEach((info) => {
      m[info.name] = info;
    });
    return m;
  }, [art.otherPlatformInfos]);

  // drafts[name] = { rating, votes } — we always render controlled inputs
  // that fall back to the server value; local drafts win once the admin
  // types something.
  const [drafts, setDrafts] = useState({});
  const [savingPlatform, setSavingPlatform] = useState(null); // "amazon" while in flight
  const [refetching, setRefetching] = useState(false);
  const [refetchResult, setRefetchResult] = useState(null); // null = closed, object = modal open

  const handleRefetch = () => {
    setRefetching(true);
    axiosInstance
      .post(`/admin/arts/${art.id}/refetchMovieRatings`)
      .then((res) => {
        setRefetchResult(res.data || {});
        onArtUpdated && onArtUpdated();
      })
      .catch((err) => {
        console.error('Refetch failed', err);
        alert('Refetch failed: ' + (err.response?.data || err.message));
      })
      .finally(() => setRefetching(false));
  };

  const updateDraft = (name, patch) => {
    setDrafts((prev) => ({ ...prev, [name]: { ...(prev[name] || {}), ...patch } }));
  };

  const currentValues = (name) => {
    const info = infosByName[name];
    const draft = drafts[name] || {};
    return {
      rating: draft.rating !== undefined ? draft.rating : (info?.score ?? ''),
      votes: draft.votes !== undefined ? draft.votes : (info?.votes ?? ''),
    };
  };

  const handleSave = (name) => {
    const { rating, votes } = currentValues(name);
    const ratingNum = Number(rating);
    const votesNum = Number(votes || 0);
    if (!Number.isFinite(ratingNum) || ratingNum < 0) {
      alert('Rating must be a non-negative number.');
      return;
    }
    setSavingPlatform(name);
    axiosInstance
      .post('/admin/rating', { artId: art.id, platform: name, rating: ratingNum, votesCount: votesNum })
      .then(() => {
        setDrafts((prev) => ({ ...prev, [name]: undefined }));
        onArtUpdated && onArtUpdated();
      })
      .catch((err) => {
        console.error('Save rating failed', err);
        alert('Save failed: ' + (err.response?.data || err.message));
      })
      .finally(() => setSavingPlatform(null));
  };

  const handleDelete = (name) => {
    if (!window.confirm(`Delete ${name} rating?`)) return;
    setSavingPlatform(name);
    axiosInstance
      .post('/admin/rating', { artId: art.id, platform: name, delete: true })
      .then(() => {
        setDrafts((prev) => ({ ...prev, [name]: undefined }));
        onArtUpdated && onArtUpdated();
      })
      .catch((err) => {
        console.error('Delete rating failed', err);
        alert('Delete failed: ' + (err.response?.data || err.message));
      })
      .finally(() => setSavingPlatform(null));
  };

  const isMovie = art.category === 1;

  const refetchModal = (
    <Modal
      opened={refetchResult !== null}
      onClose={() => setRefetchResult(null)}
      title="Fresh ratings"
      centered
      size="md"
    >
      <RefetchResultBody result={refetchResult || {}} />
    </Modal>
  );

  if (!open) {
    return (
      <>
        <div className="mt-2 flex justify-center gap-2 flex-wrap">
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setOpen(true)}>
            Edit ratings
          </Button>
          <AdminRatingHistoryButton artId={art.id} />
          {isMovie && (
            <Button size="compact-xs" variant="subtle" color="gray" loading={refetching} onClick={handleRefetch}>
              Refetch KP / RT
            </Button>
          )}
        </div>
        {refetchModal}
      </>
    );
  }

  return (
    <div className="mt-3 border rounded-md p-3" style={{ borderColor: 'var(--mantine-color-default-border)' }}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <Text size="xs" c="dimmed">Admin: platform ratings</Text>
        <div className="flex gap-2">
          {isMovie && (
            <Button size="compact-xs" variant="subtle" color="gray" loading={refetching} onClick={handleRefetch}>
              Refetch KP / RT
            </Button>
          )}
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setOpen(false)}>
            Hide
          </Button>
        </div>
      </div>
      {refetchModal}
      <div className="flex flex-col gap-1">
        {PLATFORMS.map(({ name, label, icon }) => {
          const { rating, votes } = currentValues(name);
          const existing = infosByName[name];
          const busy = savingPlatform === name;
          return (
            <div key={name} className="flex items-center gap-2 flex-wrap">
              <img src={icon} alt={label} className="w-4 h-4 rounded-sm" />
              <span className="text-xs w-40 truncate" title={label}>{label}</span>
              <NumberInput
                size="xs"
                w={90}
                placeholder="rating"
                value={rating}
                min={0}
                step={0.1}
                decimalScale={2}
                hideControls
                onChange={(v) => updateDraft(name, { rating: v })}
              />
              <NumberInput
                size="xs"
                w={100}
                placeholder="votes"
                value={votes}
                min={0}
                hideControls
                onChange={(v) => updateDraft(name, { votes: v })}
              />
              <Button size="compact-xs" variant="light" loading={busy} onClick={() => handleSave(name)}>
                Save
              </Button>
              {existing && existing.score !== undefined && (
                <Button size="compact-xs" variant="subtle" color="red" loading={busy} onClick={() => handleDelete(name)}>
                  Delete
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Renders one row per platform present in the /refetchMovieRatings
// response, plus any error strings the backend reported so an admin
// can see whether a source silently failed.
const RefetchResultBody = ({ result }) => {
  const rows = [
    { key: 'kinopoisk', label: 'Kinopoisk' },
    { key: 'imdb', label: 'IMDb' },
    { key: 'rottenTomatoes', label: 'Rotten Tomatoes (critics)' },
    { key: 'rottenTomatoesAudience', label: 'Rotten Tomatoes (audience)' },
  ];
  const anyValue = rows.some(({ key }) => result[key]);
  return (
    <div className="flex flex-col gap-2">
      {!anyValue && !result.kinopoiskError && !result.rottenTomatoesError && (
        <Text size="sm" c="dimmed">No ratings returned. The art may lack Kinopoisk / RT links.</Text>
      )}
      {rows.map(({ key, label }) => {
        const v = result[key];
        if (!v) return null;
        return (
          <div key={key} className="flex justify-between">
            <Text size="sm">{label}</Text>
            <Text size="sm" fw={600}>
              {v.rating}{v.votes ? ` (${v.votes.toLocaleString()} votes)` : ''}
            </Text>
          </div>
        );
      })}
      {result.kinopoiskError && (
        <Text size="xs" c="red">Kinopoisk error: {result.kinopoiskError}</Text>
      )}
      {result.rottenTomatoesError && (
        <Text size="xs" c="red">Rotten Tomatoes error: {result.rottenTomatoesError}</Text>
      )}
      {result.kinopoiskUrl && (
        <Text size="xs" c="dimmed">KP source: {result.kinopoiskUrl}</Text>
      )}
      {result.rottenTomatoesUrl && (
        <Text size="xs" c="dimmed">RT source: {result.rottenTomatoesUrl}</Text>
      )}
    </div>
  );
};

export default AdminRatingsEditor;
